import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import AppWrapper from './AppWrapper';
import ConnectionStatus from './components/ConnectionStatus';
import ErrorBoundary from './components/ErrorBoundary';
// Side-effect imports — these modules attach console helpers
// (window.__dfHealth, window.__dfRepair, window.__dfErrorLog) on
// module load. No exports needed at the top level, just the import.
import './lib/firmLinkHealth';
import './lib/errorLog';
import { AuthProvider } from './contexts/AuthContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { PushNotificationsProvider } from './contexts/PushNotificationsContext';

// ── Stale service-worker cleanup ──────────────────────────────────────
// In development we never want a SW registered (CRA's hot-reload + SW
// install causes an infinite reload loop). This runs before any React
// code, so even a previously-broken dev SW gets unregistered on the
// very first bundle load. In production the SW is intentional and
// stays registered.
if (process.env.NODE_ENV !== 'production' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations()
    .then((regs) => {
      if (regs.length === 0) return;
      // eslint-disable-next-line no-console
      console.log('[sw-cleanup] Unregistering', regs.length, 'stale service worker(s) for dev');
      return Promise.all(regs.map((r) => r.unregister()));
    })
    .then(() => {
      // Also blow away any caches the old SW populated. Otherwise next
      // load still serves the old broken bundle from cache before the
      // dev server can respond.
      if (window.caches) {
        return caches.keys().then((keys) =>
          Promise.all(keys.map((k) => caches.delete(k)))
        );
      }
      return null;
    })
    .catch(() => { /* best-effort */ });
}

// ResizeObserver error suppression has been moved to public/index.html (inline
// <script> in <head>) so it runs BEFORE react-error-overlay registers its
// own error listener. Doing it here was too late — the overlay already caught
// the event by the time this module loaded.

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  // ErrorBoundary is the OUTERMOST node so it catches crashes anywhere
  // in the tree — including AuthProvider startup failures, the route
  // dispatcher in AppWrapper, the LoginPage itself, and PortalApp /
  // SuperAdminApp. There's a second ErrorBoundary inside App.jsx that
  // catches errors inside the main firm shell; this outer one is the
  // last line of defence so a render error never ships a blank page.
  // The boundary also installs window-level error / unhandled-rejection
  // handlers on mount so async failures get captured to localStorage
  // for post-mortem.
  <ErrorBoundary>
    <AuthProvider>
      <ImpersonationProvider>
        <PushNotificationsProvider>
          <React.StrictMode>
            {/* Top-of-viewport connection banner. Renders nothing when online
                and on a fast link — appears as a thin red/amber bar otherwise. */}
            <ConnectionStatus />
            <AppWrapper />
          </React.StrictMode>
        </PushNotificationsProvider>
      </ImpersonationProvider>
    </AuthProvider>
  </ErrorBoundary>
);
